// src/pages/Reviews.js
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { FaStar, FaTrash, FaRegStar, FaQuoteLeft, FaBox, FaUser } from 'react-icons/fa';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/reviews`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load reviews');
      const data = await res.json();
      setReviews(data);
    } catch (err) {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/reviews/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete review');
      toast.success('Review deleted successfully');
      fetchReviews();
    } catch (err) {
      toast.error('Failed to delete review');
    }
  };

  const StarDisplay = ({ rating }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        star <= rating ? (
          <FaStar key={star} className="text-amber-400 text-sm" />
        ) : (
          <FaRegStar key={star} className="text-amber-400 text-sm" />
        )
      ))}
    </div>
  );

  const getAverageRating = () => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  const getRatingDistribution = () => {
    const dist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(r => {
      if (dist[r.rating] !== undefined) dist[r.rating]++;
    });
    return dist;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium">Loading reviews...</p>
        </div>
      </div>
    );
  }

  const ratingDist = getRatingDistribution();

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-2xl font-bold text-slate-800">Customer Reviews</h1>
        <p className="text-slate-500 mt-1">{reviews.length} total reviews</p>
      </motion.div>

      {/* Stats Section */}
      {reviews.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-card border border-slate-100 p-6 mb-8"
        >
          <div className="grid md:grid-cols-2 gap-8">
            {/* Average Rating */}
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-5xl font-bold text-slate-800">{getAverageRating()}</p>
                <div className="flex justify-center mt-2">
                  <StarDisplay rating={Math.round(getAverageRating())} />
                </div>
                <p className="text-sm text-slate-500 mt-1">{reviews.length} reviews</p>
              </div>
              <div className="flex-1 space-y-2">
                {[5, 4, 3, 2, 1].map((star) => (
                  <div key={star} className="flex items-center gap-2">
                    <span className="text-sm text-slate-600 w-3">{star}</span>
                    <FaStar className="text-amber-400 text-xs" />
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-400 rounded-full transition-all duration-500"
                        style={{ width: `${reviews.length > 0 ? (ratingDist[star] / reviews.length) * 100 : 0}%` }}
                      />
                    </div>
                    <span className="text-sm text-slate-500 w-8">{ratingDist[star]}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                <p className="text-sm text-emerald-600 font-medium">5-Star Reviews</p>
                <p className="text-2xl font-bold text-emerald-700 mt-1">{ratingDist[5]}</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                <p className="text-sm text-amber-600 font-medium">4-Star Reviews</p>
                <p className="text-2xl font-bold text-amber-700 mt-1">{ratingDist[4]}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <p className="text-sm text-blue-600 font-medium">Average Rating</p>
                <p className="text-2xl font-bold text-blue-700 mt-1">{getAverageRating()}</p>
              </div>
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                <p className="text-sm text-purple-600 font-medium">Total Reviews</p>
                <p className="text-2xl font-bold text-purple-700 mt-1">{reviews.length}</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16 bg-white rounded-xl shadow-card border border-slate-100"
        >
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaStar className="text-2xl text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No reviews yet</h3>
          <p className="text-slate-500 max-w-md mx-auto">
            Customer reviews will appear here once they start reviewing delivered orders.
          </p>
        </motion.div>
      ) : (
        <div className="grid gap-4">
          {reviews.map((review, index) => (
            <motion.div
              key={review._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl shadow-card border border-slate-100 overflow-hidden hover:shadow-soft transition-all duration-300"
            >
              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">
                            {review.customerName?.charAt(0).toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800">{review.customerName}</h3>
                          <p className="text-sm text-slate-500">
                            {new Date(review.createdAt).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-100">
                        <StarDisplay rating={review.rating} />
                        <span className="text-sm font-semibold text-amber-700">{review.rating}.0</span>
                      </div>
                    </div>

                    {/* Product Info */}
                    <div className="flex flex-wrap gap-3 mb-4">
                      <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg">
                        <FaBox className="text-slate-400 text-sm" />
                        <span className="text-sm text-slate-700">{review.productName}</span>
                      </div>
                      {review.order && (
                        <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg">
                          <span className="text-sm text-slate-500">Order:</span>
                          <span className="text-sm font-medium text-slate-700">
                            #{(review.order._id || review.order).toString().slice(-8).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Review Description */}
                    {review.description && (
                      <div className="relative bg-slate-50 p-4 rounded-xl">
                        <FaQuoteLeft className="absolute top-3 left-3 text-slate-200 text-lg" />
                        <p className="text-slate-700 italic pl-6 leading-relaxed">
                          {review.description}
                        </p>
                      </div>
                    )}

                    {/* User Info */}
                    {review.user && (
                      <div className="flex items-center gap-2 mt-3 text-sm text-slate-500">
                        <FaUser className="text-xs" />
                        <span>{review.user.email || 'N/A'}</span>
                      </div>
                    )}
                  </div>

                  {/* Delete Button */}
                  <button
                    onClick={() => handleDelete(review._id)}
                    className="p-2.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl transition-colors"
                    title="Delete review"
                  >
                    <FaTrash className="text-sm" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Reviews;
